import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './button';
import { Menu, X } from 'lucide-react';
import { CurrencySelector } from '../CurrencySelector';
import { LANGUAGES } from '../../utils/i18n';

export const MainNav = ({ onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [locale, setLocale] = useState(() => localStorage.getItem('locale') || 'de');
  
  const isOnFMS = location.pathname === '/fms';
  const isOnDiagnose = location.pathname === '/';

  const switchLanguage = (langCode) => {
    setLocale(langCode);
    localStorage.setItem('locale', langCode);
    // Reload to apply translations
    window.location.reload();
  };

  // Get translated labels based on locale
  const labels = {
    de: { features: 'Features', prices: 'Preise', compare: 'Vergleich', faq: 'FAQ', login: 'Anmelden', tryFree: 'Kostenlos testen' },
    en: { features: 'Features', prices: 'Pricing', compare: 'Compare', faq: 'FAQ', login: 'Login', tryFree: 'Try for free' },
    it: { features: 'Funzioni', prices: 'Prezzi', compare: 'Confronto', faq: 'FAQ', login: 'Accedi', tryFree: 'Prova gratis' },
    fr: { features: 'Fonctions', prices: 'Tarifs', compare: 'Comparer', faq: 'FAQ', login: 'Connexion', tryFree: 'Essai gratuit' }
  };
  const t = labels[locale] || labels.de;

  return (
    <nav 
      className="fixed top-0 left-0 right-0 z-[100] backdrop-blur-sm" 
      style={{ 
        backgroundColor: 'rgba(11, 15, 20, 0.95)',
        borderBottom: '1px solid #1F2937'
      }}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
        <div className="flex justify-between items-center w-full">
          {/* Logo */}
          <div 
            className="text-xl md:text-2xl font-bold cursor-pointer flex-shrink-0"
            style={{ color: '#7CD4FD', fontFamily: 'Manrope' }}
            onClick={() => navigate('/')}
          >
            CarCortex-AI
          </div>

          {/* Desktop Toggle & Menu - Hidden on Mobile */}
          <div className="hidden md:flex items-center gap-6">
            {/* Toggle Diagnose/Flottenmanagement */}
            <div 
              className="flex rounded-xl p-1 border border-white/10"
              style={{ backgroundColor: '#1F2937' }}
            >
              <button
                onClick={() => {
                  navigate('/');
                  window.scrollTo(0, 0);
                }}
                className="px-5 py-2 rounded-lg transition-all text-sm font-medium"
                style={{
                  backgroundColor: isOnDiagnose ? '#1EA0E6' : 'transparent',
                  color: isOnDiagnose ? '#ffffff' : '#9CA3AF'
                }}
              >
                CarCortex
              </button>
              <button
                onClick={() => {
                  navigate('/fms');
                  window.scrollTo(0, 0);
                }}
                className="px-5 py-2 rounded-lg transition-all text-sm font-medium"
                style={{
                  backgroundColor: isOnFMS ? '#22C55E' : 'transparent',
                  color: isOnFMS ? '#ffffff' : '#9CA3AF'
                }}
              >
                FMS
              </button>
            </div>

            {/* Navigation Links */}
            <a 
              href={isOnFMS ? "#features" : "#features"}
              className="hover:text-blue-400 transition"
              style={{ color: '#E6EDF3' }}
            >
              {t.features}
            </a>
            <a 
              href={isOnFMS ? "#pricing" : "#pricing"}
              className="hover:text-blue-400 transition"
              style={{ color: '#E6EDF3' }}
            >
              {t.prices}
            </a>
            {!isOnFMS && (
              <>
                <a 
                  href="#vergleich"
                  className="hover:text-blue-400 transition"
                  style={{ color: '#E6EDF3' }}
                >
                  {t.compare}
                </a>
                <a 
                  href="#faq"
                  className="hover:text-blue-400 transition"
                  style={{ color: '#E6EDF3' }}
                >
                  {t.faq}
                </a>
              </>
            )}

            {/* Language Selector - Inline, elegant & small */}
            <div className="flex items-center text-xs tracking-wide" data-testid="landing-language-switcher">
              {Object.values(LANGUAGES).map((lang, idx) => (
                <React.Fragment key={lang.code}>
                  <button
                    onClick={() => switchLanguage(lang.code)}
                    className={`px-1 transition-all ${
                      locale === lang.code 
                        ? 'text-cyan-400 font-medium' 
                        : 'text-gray-500 hover:text-gray-300'
                    }`}
                  >
                    {lang.code.toUpperCase()}
                  </button>
                  {idx < Object.values(LANGUAGES).length - 1 && (
                    <span className="text-gray-600 text-[10px]">|</span>
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Currency Selector */}
            <CurrencySelector />

            {/* Auth Buttons */}
            <Button
              onClick={() => {
                const token = localStorage.getItem('token');
                if (token) {
                  navigate('/dashboard');
                } else {
                  navigate('/auth');
                }
              }}
              variant="ghost"
              style={{ color: '#E6EDF3' }}
            >
              {t.login}
            </Button>
            <Button
              onClick={() => navigate('/signup')}
              style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
            >
              {t.tryFree}
            </Button>
          </div>

          {/* Mobile Menu Button - Visible only on Mobile */}
          <button
            className="md:hidden flex items-center justify-center p-2 rounded-lg"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{ color: '#7CD4FD', backgroundColor: mobileMenuOpen ? '#1F2937' : 'transparent' }}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div 
            className="md:hidden mt-4 py-4 space-y-4"
            style={{ borderTop: '1px solid #1F2937' }}
          >
            {/* Mobile Toggle - CarCortex / FMS */}
            <div className="flex rounded-xl p-1 border border-white/10 mb-4" style={{ backgroundColor: '#1F2937' }}>
              <button
                onClick={() => {
                  navigate('/');
                  setMobileMenuOpen(false);
                  window.scrollTo(0, 0);
                }}
                className="flex-1 px-4 py-3 rounded-lg transition-all font-semibold text-sm"
                style={{
                  backgroundColor: isOnDiagnose ? '#1EA0E6' : 'transparent',
                  color: isOnDiagnose ? '#ffffff' : '#9CA3AF'
                }}
              >
                CarCortex
              </button>
              <button
                onClick={() => {
                  navigate('/fms');
                  setMobileMenuOpen(false);
                  window.scrollTo(0, 0);
                }}
                className="flex-1 px-4 py-3 rounded-lg transition-all font-semibold text-sm"
                style={{
                  backgroundColor: isOnFMS ? '#22C55E' : 'transparent',
                  color: isOnFMS ? '#ffffff' : '#9CA3AF'
                }}
              >
                FMS
              </button>
            </div>

            {/* Mobile Navigation Links */}
            <div className="space-y-3 mb-4">
              <a 
                href={isOnFMS ? "#features" : "#features"}
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-2 rounded-lg transition-all"
                style={{ color: '#E6EDF3', backgroundColor: '#1F2937' }}
              >
                {t.features}
              </a>
              <a 
                href={isOnFMS ? "#pricing" : "#pricing"}
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-2 rounded-lg transition-all"
                style={{ color: '#E6EDF3', backgroundColor: '#1F2937' }}
              >
                {t.prices}
              </a>
              {!isOnFMS && (
                <>
                  <a 
                    href="#vergleich"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-4 py-2 rounded-lg transition-all"
                    style={{ color: '#E6EDF3', backgroundColor: '#1F2937' }}
                  >
                    {t.compare}
                  </a>
                  <a 
                    href="#faq"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-4 py-2 rounded-lg transition-all"
                    style={{ color: '#E6EDF3', backgroundColor: '#1F2937' }}
                  >
                    {t.faq}
                  </a>
                </>
              )}
            </div>

            {/* Mobile Language & Currency */}
            <div className="flex items-center justify-between gap-4 mb-4">
              <span style={{ color: '#94A3B8', fontSize: '14px' }}>
                {locale === 'de' ? 'Sprache' : locale === 'en' ? 'Language' : locale === 'it' ? 'Lingua' : 'Langue'}:
              </span>
              <div className="flex gap-1">
                {Object.values(LANGUAGES).map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => switchLanguage(lang.code)}
                    className={`w-9 h-9 rounded-lg flex items-center justify-center text-lg transition-all ${
                      locale === lang.code 
                        ? 'bg-cyan-500/20 border border-cyan-500/50' 
                        : 'bg-white/5 hover:bg-white/10 border border-transparent'
                    }`}
                    title={lang.name}
                  >
                    {lang.flag}
                  </button>
                ))}
              </div>
            </div>

            {/* Mobile Auth Buttons */}
            <div className="flex items-center justify-between gap-4 mb-4">
              <span style={{ color: '#94A3B8', fontSize: '14px' }}>
                {locale === 'de' ? 'Währung' : locale === 'en' ? 'Currency' : locale === 'it' ? 'Valuta' : 'Devise'}:
              </span>
              <CurrencySelector />
            </div>
            <Button
              onClick={() => {
                const token = localStorage.getItem('token');
                if (token) {
                  navigate('/dashboard');
                } else {
                  navigate('/auth');
                }
                setMobileMenuOpen(false);
              }}
              variant="outline"
              className="w-full"
              style={{ borderColor: '#7CD4FD', color: '#7CD4FD' }}
            >
              {t.login}
            </Button>
            <Button
              onClick={() => {
                navigate('/signup');
                setMobileMenuOpen(false);
              }}
              className="w-full"
              style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
            >
              {t.tryFree}
            </Button>
          </div>
        )}
      </div>
    </nav>
  );
};
